/**
 * Direct screenshot service that captures exactly what's visible in the viewer
 * This approach uses a technique of drawing all elements onto a single canvas
 */
class DirectScreenshotService {
  /**
   * Capture content from any viewer directly to clipboard (with fallbacks)
   * @param {HTMLElement} containerElement - The container element
   * @param {boolean} debug - Whether to output debug info
   * @returns {Promise<{status: "copied"|"downloaded"|"failed", fileName?: string, blob?: Blob|null, reason?: string, error?: Error}>}
   */
  async captureToClipboard(containerElement, debug = false) {
    let fileName = null;
    let blob = null;

    try {
      if (!containerElement) {
        return { status: "failed", reason: "no-container" };
      }

      if (debug) {
      }

      // Create a temporary canvas
      const tempCanvas = document.createElement("canvas");
      const ctx = tempCanvas.getContext("2d");

      // Get container dimensions
      const width = containerElement.clientWidth;
      const height = containerElement.clientHeight;

      if (width <= 0 || height <= 0) {
        return { status: "failed", reason: "invalid-dimensions" };
      }

      // Set canvas size to match container
      tempCanvas.width = width;
      tempCanvas.height = height;

      if (debug) {
      }

      // First, try to draw the background
      try {
        const bgColor = getComputedStyle(containerElement).backgroundColor;
        if (
          bgColor &&
          bgColor !== "rgba(0, 0, 0, 0)" &&
          bgColor !== "transparent"
        ) {
          ctx.fillStyle = bgColor;
          ctx.fillRect(0, 0, width, height);
        }
      } catch (e) {
        if (debug) console.warn("Could not fill background:", e);
      }

      // Draw all canvas elements
      const canvases = containerElement.querySelectorAll("canvas");

      for (const canvas of canvases) {
        try {
          // Calculate dimensions that preserve aspect ratio
          const dimensions = this.calculatePreservedDimensions(
            canvas.width,
            canvas.height,
            width,
            height
          );

          ctx.drawImage(
            canvas,
            dimensions.x,
            dimensions.y,
            dimensions.width,
            dimensions.height
          );

        } catch (e) {
          if (debug) console.warn("Failed to draw canvas:", e);
        }
      }

      // Draw all images
      const images = containerElement.querySelectorAll("img");

      for (const img of images) {
        try {
          // Only draw completed images
          if (img.complete) {
            // Get the computed style to respect object-fit
            const imgStyle = window.getComputedStyle(img);
            const objectFit = imgStyle.objectFit;

            // Calculate dimensions based on natural size and object-fit
            const dimensions = this.calculateImageDimensions(
              img.naturalWidth,
              img.naturalHeight,
              width,
              height,
              objectFit
            );

            ctx.drawImage(
              img,
              dimensions.x,
              dimensions.y,
              dimensions.width,
              dimensions.height
            );

          } else {
          }
        } catch (e) {
          if (debug) console.warn("Failed to draw image:", e);
        }
      }

      // Create a PNG blob
      blob = await this.createCanvasBlob(tempCanvas, debug);

      if (!blob || blob.size <= 100) {
        return { status: "failed", reason: "empty-blob", fileName, blob };
      }

      fileName = this.buildFileName();

      // Attempt clipboard write when supported
      const clipboardResult = await this.writeImageToClipboard(blob, debug);
      if (clipboardResult.success) {
        return { status: "copied", fileName, blob };
      }

      if (clipboardResult.error && debug) {
      }

      // Fallback: trigger download so Safari (and other browsers) still get the image
      const downloadResult = this.downloadBlob(blob, fileName, debug);
      if (downloadResult.success) {
        if (debug) {
        }
        return {
          status: "downloaded",
          fileName: downloadResult.fileName,
          blob,
        };
      }

      if (debug && downloadResult.error) {
      }

      return {
        status: "failed",
        reason: "clipboard-and-download-failed",
        fileName,
      };
    } catch (error) {
      return { status: "failed", reason: "exception", error, fileName, blob };
    }
  }

  /**
   * Calculate dimensions that preserve aspect ratio inside a container
   * @param {number} srcWidth - Source width
   * @param {number} srcHeight - Source height
   * @param {number} maxWidth - Container width
   * @param {number} maxHeight - Container height
   * @returns {Object} - Dimensions object with x, y, width, height
   */
  calculatePreservedDimensions(srcWidth, srcHeight, maxWidth, maxHeight) {
    const srcRatio = srcWidth / srcHeight;
    const maxRatio = maxWidth / maxHeight;

    let width, height, x, y;

    if (srcRatio > maxRatio) {
      // Source is wider than container (relative to height)
      width = maxWidth;
      height = width / srcRatio;
      x = 0;
      y = (maxHeight - height) / 2; // Center vertically
    } else {
      // Source is taller than container (relative to width)
      height = maxHeight;
      width = height * srcRatio;
      y = 0;
      x = (maxWidth - width) / 2; // Center horizontally
    }

    return { x, y, width, height };
  }

  /**
   * Calculate image dimensions based on object-fit style
   * @param {number} imgWidth - Natural image width
   * @param {number} imgHeight - Natural image height
   * @param {number} containerWidth - Container width
   * @param {number} containerHeight - Container height
   * @param {string} objectFit - CSS object-fit value
   * @returns {Object} - Dimensions object with x, y, width, height
   */
  calculateImageDimensions(
    imgWidth,
    imgHeight,
    containerWidth,
    containerHeight,
    objectFit
  ) {
    // Default dimensions (for 'fill' or other unhandled values)
    let dimensions = {
      x: 0,
      y: 0,
      width: containerWidth,
      height: containerHeight,
    };

    if (objectFit === "cover") {
      // Cover - maintain aspect ratio but fill the container
      dimensions = this.calculatePreservedDimensions(
        imgWidth,
        imgHeight,
        containerWidth,
        containerHeight
      );

      // For cover, we need to ensure the image fills the container
      // while maintaining aspect ratio, potentially cropping
      const imgRatio = imgWidth / imgHeight;
      const containerRatio = containerWidth / containerHeight;

      if (imgRatio > containerRatio) {
        // Image is wider than container (relative to height)
        dimensions.height = containerHeight;
        dimensions.width = containerHeight * imgRatio;
        dimensions.y = 0;
        dimensions.x = (containerWidth - dimensions.width) / 2;
      } else {
        // Image is taller than container (relative to width)
        dimensions.width = containerWidth;
        dimensions.height = containerWidth / imgRatio;
        dimensions.x = 0;
        dimensions.y = (containerHeight - dimensions.height) / 2;
      }
    } else if (objectFit === "contain") {
      // Contain - maintain aspect ratio to fit within container
      dimensions = this.calculatePreservedDimensions(
        imgWidth,
        imgHeight,
        containerWidth,
        containerHeight
      );
    } else if (objectFit === "none") {
      // None - use original size
      dimensions.width = imgWidth;
      dimensions.height = imgHeight;
      dimensions.x = (containerWidth - imgWidth) / 2;
      dimensions.y = (containerHeight - imgHeight) / 2;
    }

    return dimensions;
  }

  /**
   * Attempt to create a blob from a canvas, falling back to data URL conversion for browsers
   * that lack HTMLCanvasElement.toBlob (older Safari)
   * @param {HTMLCanvasElement} canvas
   * @param {boolean} debug
   * @returns {Promise<Blob|null>}
   */
  async createCanvasBlob(canvas, debug) {
    if (typeof canvas.toBlob === "function") {
      return await new Promise(resolve =>
        canvas.toBlob(resolve, "image/png", 1.0)
      );
    }

    if (debug) {
      console.warn("canvas.toBlob not available; using dataURL fallback");
    }

    try {
      const dataURL = canvas.toDataURL("image/png", 1.0);
      const byteString = atob(dataURL.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i += 1) {
        intArray[i] = byteString.charCodeAt(i);
      }

      return new Blob([intArray], { type: "image/png" });
    } catch (error) {
      return null;
    }
  }

  /**
   * Determine if the current browser can write images to the clipboard
   * @returns {boolean}
   */
  isClipboardImageSupported() {
    return (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.write === "function" &&
      typeof window !== "undefined" &&
      typeof window.ClipboardItem === "function"
    );
  }

  /**
   * Try to write the blob to the clipboard if the browser allows it
   * @param {Blob} blob
   * @param {boolean} debug
   * @returns {Promise<{success: boolean, error?: Error}>}
   */
  async writeImageToClipboard(blob, debug) {
    if (!this.isClipboardImageSupported()) {
      if (debug) {
        console.warn(
          "Clipboard API for images not supported; will fallback to download"
        );
      }
      return { success: false };
    }

    try {
      const ClipboardItemCtor = window.ClipboardItem;
      const item = new ClipboardItemCtor({
        [blob.type || "image/png"]: blob,
      });
      await navigator.clipboard.write([item]);
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Trigger a download for the provided blob as a PNG file.
   * @param {Blob} blob
   * @param {boolean} debug
   * @returns {{success: boolean, fileName?: string, error?: Error}}
   */
  downloadBlob(blob, fileName, debug) {
    try {
      if (!blob) {
        throw new Error("No blob provided for download");
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const resolvedFileName = fileName || this.buildFileName();

      link.href = url;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (debug) {
      }

      return { success: true, fileName: resolvedFileName };
    } catch (error) {
      return { success: false, error };
    }
  }

  /**
   * Generate a filename for downloaded screenshots
   * @returns {string}
   */
  buildFileName() {
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .split("Z")[0];
    return `screenshot_${timestamp}.png`;
  }

  /**
   * Show a notification message
   * @param {string} message - The message to show
   * @param {string} type - Message type (success, error, info)
   */
  showNotification(message, type = "info") {
    // Create notification element
    const notification = document.createElement("div");
    notification.textContent = message;
    notification.style.position = "fixed";
    notification.style.bottom = "20px";
    notification.style.right = "20px";
    notification.style.padding = "10px 20px";
    notification.style.borderRadius = "4px";
    notification.style.color = "white";
    notification.style.fontSize = "14px";
    notification.style.zIndex = "9999";
    notification.style.boxShadow = "0 2px 10px rgba(0,0,0,0.2)";

    // Set background color based on type
    switch (type) {
      case "success":
        notification.style.backgroundColor = "rgba(46, 125, 50, 0.9)";
        break;
      case "error":
        notification.style.backgroundColor = "rgba(198, 40, 40, 0.9)";
        break;
      default:
        notification.style.backgroundColor = "rgba(33, 150, 243, 0.9)";
    }

    // Add to document
    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.5s ease";

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    }, 3000);
  }
}

// Create singleton instance
const directScreenshotService = new DirectScreenshotService();
export default directScreenshotService;
